import React, { useEffect, useState, useRef, useCallback } from "react";
import { Avatar, Box, Button, Card, CardContent, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Divider, FormHelperText, Grid, IconButton, MenuItem, TextField, Typography, useMediaQuery, Tab, Tabs, Tooltip, Badge, LinearProgress, Collapse, List, ListItem, ListItemIcon, ListItemText, AppBar, Toolbar, Drawer, Hidden, Stack, Paper, Switch, FormControlLabel, Alert, Snackbar } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { SketchPicker } from "react-color";
import EditIcon from "@mui/icons-material/Edit";
import RequestQuoteIcon from "@mui/icons-material/RequestQuote";
import PersonIcon from "@mui/icons-material/Person";
import LinkIcon from "@mui/icons-material/Link";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import PaletteIcon from "@mui/icons-material/Palette";
import ApiIcon from "@mui/icons-material/Api";
import NotificationsIcon from "@mui/icons-material/Notifications";
import TeamIcon from "@mui/icons-material/Group";
import LogoutIcon from "@mui/icons-material/Logout";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import PendingActionsIcon from "@mui/icons-material/PendingActions";
import PercentIcon from "@mui/icons-material/Percent";
import MenuIcon from "@mui/icons-material/Menu";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import WarningIcon from "@mui/icons-material/Warning";
import BusinessIcon from "@mui/icons-material/Business";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CloseIcon from "@mui/icons-material/Close";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import SecurityIcon from "@mui/icons-material/Security";
import PublicIcon from "@mui/icons-material/Public";
import StarIcon from "@mui/icons-material/Star";
import SwitchAccountIcon from "@mui/icons-material/SwitchAccount";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import InfoIcon from "@mui/icons-material/Info";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import PaymentIcon from "@mui/icons-material/Payment";
import ReceiptIcon from "@mui/icons-material/Receipt";
import { GoogleLogin } from "@react-oauth/google";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useSearchParams, useNavigate } from "react-router-dom";

import StyledTabs from "../../Components/StyledTabs";
import TabPanel from "../../Components/TabPanel";
import DeveloperPanel from "./ApiServices/developerPanel";
import UploadFileDialog from "./Users/sepcupload";
import NotifSettings from "./Users/notif_settings";
import MyTeam from "./Users/myteam";
import useAlertDialog from "../../Components/useAlertDialog";
import AlertDialog from "../../Components/AlertDialog";
import client from "../../API";
import { replaceConsoleError } from '../../Utils/toastNotifications';
import ppfLogo from "../../Components/globalAssets/ppfLogo.png";
import { switchRole } from "../../API/roleSwitcher";
import useAuth from "../../Hooks/use-auth";
import { useCreatorAuth } from "../../Hooks/creator-use-auth";
import routes from "../../Config/routes";
import UserContractView from "../Contracts/usercontractview";
import AssignmentIcon from "@mui/icons-material/Assignment";
import WalletCard from "../../CreatorUser/CreatorPages/Dashboard/editCreator/WalletCard";
import WalletDialog from "../../CreatorUser/CreatorPages/Dashboard/editCreator/WalletDialog";
import TaxInfoDialog from "../../CreatorUser/CreatorPages/Dashboard/editCreator/TaxInfoDialog";
import { useWalletManagement } from "./Users/wallets/useWalletManagement";
import { useTaxManagement } from "./Users/wallets/useTaxManagement";

const User = () => {
  const theme = useTheme();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabValue = searchParams.get("tab") || "team";
  const navigate = useNavigate();
  const { unauthorize, getCurrrentUser } = useAuth();
  const { isAuthorized: isCreatorAuthorized, user: creatorUser, isInitialized } = useCreatorAuth();
  const userInfo = getCurrrentUser();
  const [pendingContractsCount, setPendingContractsCount] = useState(0);

  // Ref for scrolling to content
  const contentRef = useRef(null);

  // Mobile responsive breakpoints
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const setTabValue = (newValue) => setSearchParams({ tab: newValue });
  const prefersDarkMode = useMediaQuery("(prefers-color-scheme: dark)");

  /* ───────── Mobile Navigation State ───────── */
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  /* ───────── Dialog States ───────── */
  const { dialogState, openDialog } = useAlertDialog();
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [spendingDialogOpen, setSpendingDialogOpen] = useState(false);
  const [legalEntityWarningOpen, setLegalEntityWarningOpen] = useState(false);
  const [legalEntityDialogOpen, setLegalEntityDialogOpen] = useState(false);
  const [legalEntityName, setLegalEntityName] = useState("");

  /* ───────── Snackbar State ───────── */
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  /* ───────── Wallet Management Hook ───────── */
  const showSnackbar = useCallback((message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
    }, []); // Empty 

 const {
   wallets,
    walletsLoading,
    walletDialog,
    newWallet,
    fetchWallets,
    handleWalletChange,
    handleDeleteWallet,
    handleSetPrimaryWallet,
    handleOpenWalletDialog,
    handleCloseWalletDialog,
    handleSubmitWallet,
  } = useWalletManagement(showSnackbar); // This now receives a stable reference

  /* ───────── Tax Management Hook ───────── */
  const {
    taxInfo,
    taxInfoLoading,
    taxDialog,
    newTaxInfo,
    fetchTaxInfo,
    handleTaxChange,
    handleSaveTaxInfo,
    handleOpenTaxDialog,
    handleCloseTaxDialog,
    isTaxInfoRequired,
    isTaxInfoSufficient,
    getTaxRequirementMessage,
  } = useTaxManagement(showSnackbar); // This also receives a stable reference

  /* ───────── User Profile State ───────── */
  const [userData, setUserData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    username: "",
    address: "",
    phphref: "",
    phoneNumber: "",
    isPrivate: false,
  });

  /* ───────── Appearance Settings ───────── */
  const [appearanceSettings, setAppearanceSettings] = useState({
    darkMode: null,
    fontPreference: "default",
    uiDensity: "normal",
    themeColors: {
      primary: prefersDarkMode ? "#cb534f" : "#7b2a2a",
      secondary: "#646aff",
    },
  });

  /* ───────── Balance State ───────── */
  const [balanceData, setBalanceData] = useState({
    balance: "Loading...",
  });

  /* ───────── Account Status Query ───────── */
  const { data: accountStatus, isLoading: statusLoading, refetch: refetchStatus } = useQuery(
    ['account-status'],
    () => client.accountLinking.getStatus(),
    {
      enabled: !!userInfo,
      staleTime: 1000 * 60 * 5,
      onError: (error) => {
        replaceConsoleError('Failed to fetch account status', error);
      }
    }
  );

  /* ───────── Context Switch Mutation ───────── */
  const switchContextMutation = useMutation(
    (contextType) => client.accountLinking.switchContext(contextType),
    {
      onSuccess: (result) => {
        if (result.requires_new_token) {
          if (result.new_context === 'creator') {
            window.location.href = '/creatorconnect/start';
          } else {
            window.location.href = '/dashboard';
          }
        } else {
          refetchStatus();
        }
      },
      onError: (error) => {
        replaceConsoleError('Failed to switch context', error);
        openDialog("Error", error?.response?.data?.error || "Failed to switch account context");
      }
    }
  );

  /* ───────── Data Queries ───────── */
  const { data: userInfoData, isLoading: userInfoLoading } = useQuery(["user"], client.users.fetchUser, {
    onSuccess: (data) => {
      if (!data) return;
      setUserData((prev) => ({
        ...prev,
        firstName: data.first_name || "",
        lastName: data.last_name || "",
        email: data.email || "",
        username: data.username || "",
        address: data.address || "",
        phphref: data.phphref || "",
        phoneNumber: data.phone_number || "",
        isPrivate: data.is_private || false,
      }));
    },
  });

  const {
    data: companyData,
    isLoading: companyLoading,
  } = useQuery(
    ["company", userInfo?.company_id],
    () => client.companies.listFetch({ id: userInfo?.company_id }),
    { enabled: !!userInfo?.company_id }
  );

  const { isLoading: balanceLoading } = useQuery(
    ["balance", "my_balance"],
    () => client.balanceLedger.fetchMyBalance(),
    {
      onSuccess: (data) => {
        if (data?.balance !== undefined) {
          setBalanceData({
            balance: `$${parseFloat(data.balance).toFixed(2)}`,
          });
        } else {
          replaceConsoleError("Unexpected balance data format", data);
          setBalanceData({
            balance: "Error loading balance",
          });
        }
      },
      onError: (error) => {
        replaceConsoleError("Error fetching balance data", error);
        setBalanceData({
          balance: "Error loading balance",
        });
      },
      refetchOnWindowFocus: false,
      staleTime: 1000 * 60 * 30,
      enabled: !!userInfo,
    }
  );

  const {
    data: ledgerEntries,
    isLoading: ledgerLoading,
    isError: ledgerError,
    error: ledgerErrorObj,
  } = useQuery(
    ["spendingLedger", userInfo?.company_id],
    () => client.creditLedger.fetch(userInfo.company_id),
    { enabled: !!userInfo?.company_id }
  );

  const { data: userSettings } = useQuery(["userSettings"], () =>
    client.users.fetchUserSettings()
  );

  const { data: teamData, isLoading: teamLoading } = useQuery("teamStructure", client.users.fetchMyTeam);

  /* ───────── Mutations ───────── */
  const saveUserMutation = useMutation(client.users.updateUser, {
    onSuccess: () => queryClient.invalidateQueries("user"),
    onError: (error) =>
      openDialog("Error", error?.response?.data?.error || error.message),
  });

  const saveSettingsMutation = useMutation(
    (payload) =>
      userSettings?.id
        ? client.users.updateUserSettings({ id: userSettings.id, ...payload })
        : client.users.createUserSettings(payload),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["userSettings", "user"]);
      },
      onError: (error) =>
        openDialog("Error", error?.response?.data?.error || error.message),
    }
  );

  const updateLegalEntityMutation = useMutation(
    (data) => client.companies.updateLegalEntity(companyData.id, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["company", userInfo?.company_id]);
        setLegalEntityDialogOpen(false);
        setLegalEntityWarningOpen(false);
        setLegalEntityName("");
        openDialog("Success", "Legal entity name updated successfully!");
      },
      onError: (error) => {
        openDialog("Error", error?.response?.data?.error || "Failed to update legal entity");
      },
    }
  );

  /* ───────── Side Effects ───────── */
  useEffect(() => {
    if (userSettings?.id) {
      setAppearanceSettings({
        darkMode: userSettings.dark_mode,
        fontPreference: userSettings.font_preference,
        uiDensity: userSettings.ui_density,
        themeColors: userSettings.theme_colors || {
          primary: "#1976d2",
          secondary: "#9c27b0",
        },
      });
    }
  }, [userSettings]);

  useEffect(() => {
    if (companyData && !companyData.legal_entity_name && !legalEntityWarningOpen) {
      setLegalEntityWarningOpen(true);
    }
  }, [companyData, legalEntityWarningOpen]);

  useEffect(() => {
    if (userInfo) {
      fetchWallets();
      fetchTaxInfo();
    }
  }, [userInfo, fetchWallets, fetchTaxInfo]);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await client.contracts.getMyPendingContracts();
        if (active) setPendingContractsCount(res?.pending_contracts?.length || res?.count || 0);
      } catch (e) {
        replaceConsoleError("contracts badge fetch failed", e);
      }
    })();
    return () => { active = false; };
  }, []);

  /* ───────── Form Handlers ───────── */
  const handleUserInfoChange = (e) => {
    const { name, value, type, checked } = e.target;
    setUserData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handlePrivacyToggle = (event) => {
    setUserData((prev) => ({
      ...prev,
      isPrivate: event.target.checked
    }));
  };

  const handleSaveUserInfo = async () => {
    await saveUserMutation.mutateAsync({
      first_name: userData.firstName,
      last_name: userData.lastName,
      email: userData.email,
      username: userData.username,
      address: userData.address,
      phphref: userData.phphref,
      phone_number: userData.phoneNumber,
      is_private: userData.isPrivate,
    });
  };

  const handleSaveAppearance = async () => {
    await saveSettingsMutation.mutateAsync({
      dark_mode: appearanceSettings.darkMode,
      font_preference: appearanceSettings.fontPreference,
      ui_density: appearanceSettings.uiDensity,
      theme_colors: appearanceSettings.themeColors,
    });
  };

  const handleLegalEntityUpdate = () => {
    if (!legalEntityName.trim()) {
      openDialog("Error", "Please enter a legal entity name");
      return;
    }
    updateLegalEntityMutation.mutate({ legal_entity_name: legalEntityName.trim() });
  };

  const handleConfigureLegalEntity = () => {
    setLegalEntityWarningOpen(false);
    setLegalEntityName(companyData?.legal_entity_name || "");
    setLegalEntityDialogOpen(true);
  };

  const handleGoToTeamSettings = () => {
    setLegalEntityWarningOpen(false);
    setTabValue("team");
  };

  const handleSwitchContext = (contextType) => {
    switchContextMutation.mutate(contextType);
  };

  const navigateToAccountManager = () => {
    navigate('/global-linking');
  };

  const handleLogout = () => {
    unauthorize();
  return window.location.href = routes.base;
  };

  const toggleMobileNav = () => setMobileNavOpen(!mobileNavOpen);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    if (isMobile) {
      setMobileNavOpen(false);
    }
    
    setTimeout(() => {
      if (contentRef.current) {
        contentRef.current.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'start'
        });
      }
    }, 100);
  };

  /* ───────── Calculate Metrics ───────── */
  const allMembers = teamData?.all_members || [];
  const teams = teamData?.teams || [];
  const activeTeamMembers = allMembers.filter(member => member.status).length;
  const totalTeamMembers = allMembers.length;
  const pendingApprovals = allMembers.filter(member => !member.status).length;
  const spendingUtilization = companyData?.spending_power_limit ?
    ((companyData.spending_power_limit - (companyData.balance || 0)) / companyData.spending_power_limit * 100) : 0;

  /* ───────── Profile Completion Calculation ───────── */
  const calculateProfileCompletion = () => {
    const criteria = [
      {
        key: 'user_profile',
        label: 'User Profile',
        completed: !!(userData.firstName && userData.lastName && userData.email && userData.username),
        weight: 20
      },
      {
        key: 'company_name',
        label: 'Company Name',
        completed: !!companyData?.name,
        weight: 15
      },
      {
        key: 'legal_entity',
        label: 'Legal Entity',
        completed: !!companyData?.legal_entity_name,
        weight: 20
      },
      {
        key: 'spending_power_limit',
        label: 'Spending Power',
        completed: !!(companyData?.spending_power_limit && companyData.spending_power_limit > 0),
        weight: 15
      },
      {
        key: 'team_members',
        label: 'Team Members',
        completed: totalTeamMembers > 0,
        weight: 15
      },
      {
        key: 'phone_number',
        label: 'Phone Number',
        completed: !!userData.phoneNumber,
        weight: 10
      },
      {
        key: 'address',
        label: 'Business Address',
        completed: !!userData.address,
        weight: 5
      }
    ];

    const totalWeight = criteria.reduce((sum, item) => sum + item.weight, 0);
    const completedWeight = criteria
      .filter(item => item.completed)
      .reduce((sum, item) => sum + item.weight, 0);

    const percentage = Math.round((completedWeight / totalWeight) * 100);
    
    return {
      percentage,
      criteria,
      completedCount: criteria.filter(item => item.completed).length,
      totalCount: criteria.length
    };
  };

  const profileCompletion = calculateProfileCompletion();

  /* ───────── Completion Tooltip Content ───────── */
  const CompletionTooltipContent = () => (
    <Box sx={{ p: 1 }}>
      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
        Profile Completion Breakdown:
      </Typography>
      {profileCompletion.criteria.map((item) => (
        <Box
          key={item.key}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            mb: 0.5
          }}
        >
          {item.completed ? (
            <CheckCircleIcon sx={{ fontSize: 16, color: '#4caf50' }} />
          ) : (
            <Box
              sx={{
                width: 16,
                height: 16,
                borderRadius: '50%',
                border: '2px solid #ff9800'
              }}
            />
          )}
          <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
            {item.label} ({item.weight}%)
          </Typography>
        </Box>
      ))}
      <Divider sx={{ my: 1 }} />
      <Typography variant="caption" sx={{ display: 'block', mt: 1, fontStyle: 'italic' }}>
        Complete all items to unlock full platform features
      </Typography>
    </Box>
  );

  const status = accountStatus?.data;
  const hasUserAccount = status?.has_user_account;
  const hasCreatorAccount = status?.has_creator_account;
  const currentContext = status?.current_context;

  // Tab configuration
  const tabItems = [
    {
      value: "team",
      icon: <Badge badgeContent={pendingApprovals || null} color="warning"><TeamIcon /></Badge>,
      label: "Team & Spending Power Hub"
    },
    {
      value: "payments",
      icon: <AccountBalanceWalletIcon />,
      label: "Payment Methods"
    },
    {
      value: "contracts",
      icon: (
        <Badge badgeContent={pendingContractsCount || null} color="warning">
          <AssignmentIcon />
        </Badge>
      ),
      label: "Contracts"
    },
    { value: "appearance", icon: <PaletteIcon />, label: "Appearance" },
    { value: "notif", icon: <NotificationsIcon />, label: "Notifications" },
    { value: "dev", icon: <ApiIcon />, label: "Developers" },
    {
      value: "accounts",
      icon: <AccountTreeIcon />,
      label: "Account Manager",
      primary: true
    },
  ];

  /* ───────── Mobile Navigation Component ───────── */
  const MobileNavigation = () => (
    <Drawer
      anchor="left"
      open={mobileNavOpen}
      onClose={() => setMobileNavOpen(false)}
      PaperProps={{
        sx: { width: 280, backgroundColor: "background.paper" }
      }}
    >
      <Box sx={{ p: 2 }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 700 }}>
          Settings
        </Typography>
        <List>
          {tabItems.map((item) => (
            <ListItem
              key={item.value}
              button
              onClick={() => handleTabChange(null, item.value)}
              sx={{
                borderRadius: 2,
                mb: item.primary ? 1 : 0.5,
                backgroundColor: tabValue === item.value ? (item.primary ? "primary.main" : "action.selected") : "transparent",
                color: tabValue === item.value && item.primary ? "black" : "text.primary",
              }}
            >
              <ListItemIcon sx={{ color: "inherit" }}>
                {item.icon}
              </ListItemIcon>
              <ListItemText
                primary={item.label}
                primaryTypographyProps={{ fontWeight: item.primary ? 600 : 400 }}
              />
            </ListItem>
          ))}
        </List>
      </Box>
    </Drawer>
  );

  /* ───────── Account Manager Tab Component ───────── */
  const AccountManagerTab = () => (
    <Box>
      <Typography
        variant={isMobile ? "h5" : "h4"}
        sx={{ fontWeight: 700, mb: 1, color: "primary.main" }}
      >
        Account Manager
      </Typography>
      <Typography
        variant="body1"
        color="text.secondary"
        sx={{ mb: 3 }}
      >
        Manage and switch between your linked accounts. Connect your creator and business profiles for seamless switching.
      </Typography>

      {statusLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <SwitchAccountIcon />
              Current Account Status
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Card 
                  variant="outlined" 
                  sx={{ 
                    height: '100%',
                    border: hasUserAccount ? '2px solid #4caf50' : '1px solid #e0e0e0',
                    backgroundColor: hasUserAccount ? '#f1f8e9' : '#fafafa'
                  }}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                      <BusinessIcon color={hasUserAccount ? 'success' : 'disabled'} />
                      <Typography variant="h6">Business Account</Typography>
                      {hasUserAccount && <Chip label="Linked" color="success" size="small" />}
                    </Box>
                    
                    {hasUserAccount ? (
                      <Box>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                          <strong>Name:</strong> {status.user_info?.first_name} {status.user_info?.last_name}
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                          <strong>Company:</strong> {status.user_info?.company_name}
                        </Typography>
                        {currentContext === 'normal' && (
                          <Chip label="Currently Active" color="primary" size="small" />
                        )}
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        No business account linked
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={6}>
                <Card 
                  variant="outlined"
                  sx={{ 
                    height: '100%',
                    border: hasCreatorAccount ? '2px solid #9c27b0' : '1px solid #e0e0e0',
                    backgroundColor: hasCreatorAccount ? '#f3e5f5' : '#fafafa'
                  }}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                      <StarIcon color={hasCreatorAccount ? 'secondary' : 'disabled'} />
                      <Typography variant="h6">Creator Account</Typography>
                      {hasCreatorAccount && <Chip label="Linked" sx={{ backgroundColor: '#9c27b0', color: 'white' }} size="small" />}
                    </Box>
                    
                    {hasCreatorAccount ? (
                      <Box>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                          <strong>Handle:</strong> @{status.creator_info?.username}
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                          <strong>Display Name:</strong> {status.creator_info?.display_name}
                        </Typography>
                        {currentContext === 'creator' && (
                          <Chip label="Currently Active" sx={{ backgroundColor: '#9c27b0', color: 'white' }} size="small" />
                        )}
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        No creator account linked
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
          </Grid>

          <Grid item xs={12}>
            <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <TrendingUpIcon />
              Account Actions
            </Typography>

            <Grid container spacing={2}>
              {hasUserAccount && hasCreatorAccount && (
                <>
                  {currentContext !== 'normal' && (
                    <Grid item xs={12} sm={6} md={4}>
                      <Button
                        variant="contained"
                        fullWidth
                        startIcon={<BusinessIcon />}
                        onClick={() => handleSwitchContext('normal')}
                        disabled={switchContextMutation.isLoading}
                        size="large"
                      >
                        Switch to Business
                      </Button>
                    </Grid>
                  )}
                  {currentContext !== 'creator' && (
                    <Grid item xs={12} sm={6} md={4}>
                      <Button
                        variant="contained"
                        color="secondary"
                        fullWidth
                        startIcon={<StarIcon />}
                        onClick={() => handleSwitchContext('creator')}
                        disabled={switchContextMutation.isLoading}
                        size="large"
                      >
                        Switch to Creator
                      </Button>
                    </Grid>
                  )}
                </>
              )}

              <Grid item xs={12} sm={6} md={4}>
                <Button
                  variant="outlined"
                  fullWidth
                  startIcon={<AccountTreeIcon />}
                  onClick={navigateToAccountManager}
                  size="large"
                  sx={{
                    borderColor: 'primary.main',
                    color: 'primary.main',
                    '&:hover': {
                      borderColor: 'primary.dark',
                      backgroundColor: 'primary.50'
                    }
                  }}
                >
                  Full Account Manager
                </Button>
              </Grid>
            </Grid>

            {!hasUserAccount && !hasCreatorAccount && (
              <Alert severity="info" sx={{ mt: 2 }}>
                You don't have any linked accounts yet. Use the Full Account Manager to create and link your accounts.
              </Alert>
            )}

            {(hasUserAccount || hasCreatorAccount) && !(hasUserAccount && hasCreatorAccount) && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                Link both business and creator accounts to enable seamless switching between contexts.
              </Alert>
            )}

            {switchContextMutation.isError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {switchContextMutation.error?.response?.data?.error || 'Failed to switch account context'}
              </Alert>
            )}
          </Grid>
        </Grid>
      )}
    </Box>
  );

  /* ───────── Render Component ───────── */
  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: 1400,
        margin: "auto",
        p: isMobile ? 1 : 3,
        backgroundColor: "background.default",
        minHeight: "100vh",
      }}
    >
      <AlertDialog alertState={dialogState} />

      {/* Clean Header with Company Info & Tabs */}
      <Box
        elevation={2}
        sx={{
          mb: 3,
          // borderRadius: 3,
          overflow: "hidden",
          // background: "linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)",
          color: "black",
        }}
      >
        {isMobile && (
          <Box sx={{ p: 2, borderBottom: "1px solid rgba(255,255,255,0.2)" }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <IconButton
                color="inherit"
                onClick={toggleMobileNav}
                sx={{ mr: 2 }}
              >
                <MenuIcon />
              </IconButton>
              <Typography variant="h6" sx={{ fontWeight: 700, flexGrow: 1 }}>
                {companyData?.name || "Company Settings"}
              </Typography>
              <IconButton color="inherit" onClick={handleLogout}>
                <LogoutIcon />
              </IconButton>
            </Box>
          </Box>
        )}

        <Box sx={{ p: isMobile ? 2 : 3 }}>
          <Box sx={{
            display: "flex",
            alignItems: "center",
            gap: isMobile ? 2 : 3,
            mb: 3,
            flexDirection: isMobile ? "column" : "row",
            textAlign: isMobile ? "center" : "left"
          }}>
            <Avatar
              src={companyData?.phphref || ppfLogo}
              sx={{
                width: isMobile ? 60 : 80,
                height: isMobile ? 60 : 80,
                border: "3px solid white",
                boxShadow: "0 4px 15px rgba(0,0,0,0.2)"
              }}
            />
            <Box sx={{ flex: 1 }}>
              <Typography
                variant={isMobile ? "h5" : "h4"}
                sx={{ fontWeight: 700, mb: 0.5 }}
              >
                {companyData?.name || "Your Company"}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  opacity: 0.9,
                  mb: 1,
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  justifyContent: isMobile ? "center" : "flex-start"
                }}
              >
                {companyData?.legal_entity_name ? (
                  <>
                    <BusinessIcon fontSize="small" />
                    {companyData.legal_entity_name}
                  </>
                ) : (
                  <>
                    <WarningIcon
                      fontSize="small"
                      sx={{ color: "warning.main" }}
                    />
                    <Box
                      component="span"
                      sx={{
                        color: "warning.main",
                        fontWeight: 600,
                        textDecoration: "underline",
                        cursor: "pointer"
                      }}
                      onClick={() => setLegalEntityWarningOpen(true)}
                    >
                      Legal entity not configured - Click to set up
                    </Box>
                  </>
                )}
              </Typography>

              <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", justifyContent: isMobile ? "center" : "flex-start" }}>
                {teamLoading ? (
                  <Chip
                    icon={<CircularProgress size={12} sx={{ color: "black" }} />}
                    label="Loading team..."
                    size="small"
                    sx={{
                      backgroundColor: "rgba(255, 255, 255, 0.2)",
                      color: "white",
                      fontWeight: 600
                    }}
                  />
                ) : totalTeamMembers > 0 ? (
                  <>
                    <Chip
                      icon={<TeamIcon />}
                      label={`${activeTeamMembers}/${totalTeamMembers} Active`}
                      size="small"
                      sx={{
                        backgroundColor: "rgba(61, 216, 66,0.6)",
                        color: "black",
                        fontWeight: 600
                      }}
                    />
                    {pendingApprovals > 0 && (
                      <Chip
                        icon={<PendingActionsIcon />}
                        label={`${pendingApprovals} Pending`}
                        size="small"
                        sx={{
                          backgroundColor: "rgba(255, 152, 0, 0.2)",
                          color: "black",
                          fontWeight: 600
                        }}
                      />
                    )}
                  </>
                ) : (
                  <Chip
                    icon={<TeamIcon />}
                    label="No team members"
                    size="small"
                    sx={{
                      backgroundColor: "rgba(158, 158, 158, 0.2)",
                      color: "black",
                      fontWeight: 600
                    }}
                  />
                )}
                
                <Tooltip
                  title={<CompletionTooltipContent />}
                  arrow
                  placement="bottom"
                  componentsProps={{
                    tooltip: {
                      sx: {
                        bgcolor: 'background.paper',
                        color: 'text.primary',
                        boxShadow: 3,
                        border: '1px solid',
                        borderColor: 'divider',
                        maxWidth: 300
                      }
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'help' }}>
                    <LinearProgress 
                      variant="determinate" 
                      value={profileCompletion.percentage} 
                      sx={{
                        width: 100,
                        height: 8,
                        borderRadius: 4,
                        backgroundColor: "rgba(0,0,0,0.3)",
                        "& .MuiLinearProgress-bar": { 
                          backgroundColor: profileCompletion.percentage === 100 ? "#4caf50" : "#601E1D" 
                        }
                      }}
                    />
                    <Typography variant="caption" sx={{ opacity: 0.9, fontSize: "0.75rem", display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      {profileCompletion.percentage}% Complete
                      <InfoIcon sx={{ fontSize: 14 }} />
                    </Typography>
                  </Box>
                </Tooltip>
              </Stack>
            </Box>

            {!isMobile && (
              <Stack direction="row" spacing={2}>
                <Button
                  variant="text"
                  color="inherit"
                  startIcon={<RequestQuoteIcon />}
                  onClick={() => setSpendingDialogOpen(true)}
                  sx={{ boxShadow: "0 4px 15px rgba(0,0,0,0.2)" }}
                >
                  Adjust Spending Power
                </Button>
                <Tooltip title="Log out">
                  <Button
                    startIcon={<LogoutIcon />}
                    onClick={handleLogout}
                    sx={{
                      color: "white",
                      borderColor: "rgba(255,255,255,0.5)",
                      backgroundColor: "primary.main",
                      "&:hover": {
                        borderColor: "white",
                        backgroundColor: "primary.main",
                      },
                    }}
                  >
                    Logout
                  </Button>
                </Tooltip>
              </Stack>
            )}
          </Box>

          <Grid container spacing={isMobile ? 1 : 2} sx={{ mb: 3 }}>
            {[
              {
                icon: <AccountBalanceWalletIcon sx={{ color: "#601E1D" }} />,
                value: balanceLoading ? <CircularProgress size={16} sx={{ color: "black" }} /> : balanceData.balance,
                label: "Balance",
                progress: null
              },
              {
                icon: <TrendingUpIcon/>,
                value: companyLoading ? <CircularProgress size={16} sx={{ color: "black" }} /> : `$${companyData?.spending_power_limit || "0"}`,
                label: "Spending Power",
                progress: spendingUtilization
              },
              {
                icon: <TeamIcon sx={{ color: "#601E1D" }} />,
                value: teamLoading ? <CircularProgress size={16} sx={{ color: "black" }} /> : activeTeamMembers,
                label: "Team Members",
                progress: null
              },
              {
                icon: <PercentIcon sx={{ color: "#601E1D" }} />,
                value: companyLoading ? <CircularProgress size={16} sx={{ color: "black" }} /> : `${companyData?.nominal_interest_rate || "0"}%`,
                label: "Discount Rate",
                progress: null
              }
            ].map((metric, index) => (
              <Grid item xs={6} md={3} key={index}>
                <Paper sx={{
                  p: isMobile ? 1 : 1.5,
                  textAlign: "center",
                  background: "rgba(0,0,0,0.1)",
                  backdropFilter: "blur(10px)",
                  border: "1px solid rgba(0,0,0,0.2)",
                  color: "#601E1D"
                }}>
                  {React.cloneElement(metric.icon, {
                    sx: { fontSize: isMobile ? 20 : 24, mb: 0.5, opacity: 0.8 }
                  })}
                  <Typography
                    variant={isMobile ? "body1" : "h6"}
                    sx={{ fontWeight: 700, mb: 0.25, minHeight: isMobile ? 24 : 32, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                  >
                    {metric.value}
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.9, fontSize: "0.7rem" }}>
                    {metric.label}
                  </Typography>
                  {metric.progress !== null && (
                    <LinearProgress
                      variant="determinate"
                      value={metric.progress}
                      sx={{
                        mt: 0.5,
                        height: 3,
                        backgroundColor: "rgba(255,255,255,0.3)",
                        "& .MuiLinearProgress-bar": { backgroundColor: "rgba(255,255,255,0.8)" }
                      }}
                    />
                  )}
                </Paper>
              </Grid>
            ))}
          </Grid>

          {!isMobile && (
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                "& .MuiTabs-indicator": {
                  backgroundColor: "#662423",
                  height: 3,
                },
                "& .MuiTab-root": {
                  color: "#601E1D/0.7",
                  fontWeight: 600,
                  "&.Mui-selected": {
                    color: "#601E1D",
                  },
                },
              }}
            >
              {tabItems.map((item) => (
                <Tab
                  key={item.value}
                  icon={item.icon}
                  label={item.label}
                  value={item.value}
                  iconPosition="start"
                />
              ))}
            </Tabs>
          )}
        </Box>
      </Box>

      {isMobile && <MobileNavigation />}

      <Box ref={contentRef}>
        {/* Team & Spending Power Hub Tab */}
        <TabPanel value={tabValue} index="team">
          <Box sx={{
            width: '100%',
            overflowX: isMobile ? 'hidden' : 'auto',
            p: isMobile ? 0 : 1
          }}>
            <Typography
              variant={isMobile ? "h5" : "h5"}
              sx={{ fontWeight: 700, mb: 1, color: "#000" }}
            >
              Team & Spending Power Management Hub
            </Typography>
            <Typography
              variant="body1"

              color="text.secondary"
              sx={{ mb: 3,fontSize: "0.9rem" }}
            >
              Manage your team members, spending power operations, contracts, and legal documentation.
            </Typography>
            <MyTeam />
          </Box>
        </TabPanel>

        {/* Payment Methods Tab */}
        <TabPanel value={tabValue} index="payments">
          <Box>
            <CardContent sx={{ p: isMobile ? 2 : 3 }}>
              <Typography
                variant={isMobile ? "h6" : "h5"}
                sx={{ mb: 1, fontWeight: 700 }}
              >
                Payment Methods
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Manage your payment methods for making payments and purchases.
              </Typography>

              {/* Wallets Section */}
              <Box sx={{ mb: 4 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6">Payment Methods</Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      variant="outlined"
                      startIcon={<AddIcon />}
                      onClick={() => handleOpenWalletDialog()}
                      disabled={walletsLoading}
                    >
                      {isMobile ? "Add" : "Add Manually"}
                    </Button>
                  </Box>
                </Box>

                {walletsLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress />
                  </Box>
                ) : wallets?.length > 0 ? (
                  wallets.map((wallet) => (
                    <WalletCard
                      key={wallet.id}
                      wallet={wallet}
                      onSetPrimary={handleSetPrimaryWallet}
                      onEdit={handleOpenWalletDialog}
                      onDelete={handleDeleteWallet}
                    />
                  ))
                ) : (
                  <Paper
                    sx={{
                      p: 4,
                      textAlign: 'center',
                      backgroundColor: 'grey.50',
                      border: '2px dashed',
                      borderColor: 'grey.300'
                    }}
                  >
                    <AccountBalanceWalletIcon sx={{ fontSize: 48, color: 'grey.400', mb: 2 }} />
                    <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                      No Payment Methods Yet
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Add a payment method to start making payments
                    </Typography>
                    <Button
                      variant="contained"
                      startIcon={<AddIcon />}
                      onClick={() => handleOpenWalletDialog()}
                    >
                      Add Your First Payment Method
                    </Button>
                  </Paper>
                )}
              </Box>

              <Divider sx={{ my: 3 }} />

              {/* Tax Information Section */}
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6">Tax Information</Typography>
                  <Button
                    variant="outlined"
                    startIcon={<EditIcon />}
                    onClick={handleOpenTaxDialog}
                    disabled={taxInfoLoading}
                  >
                    {taxInfo ? 'Edit Tax Info' : 'Add Tax Info'}
                  </Button>
                </Box>

                {taxInfoLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress />
                  </Box>
                ) : taxInfo ? (
                  <Card variant="outlined">
                    <CardContent>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body2" color="text.secondary">Tax ID Type</Typography>
                          <Typography variant="body1">{taxInfo.tax_id_type?.toUpperCase()}</Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body2" color="text.secondary">Business Name</Typography>
                          <Typography variant="body1">{taxInfo.business_name || taxInfo.legal_name || 'N/A'}</Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body2" color="text.secondary">Country</Typography>
                          <Typography variant="body1">{taxInfo.country}</Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body2" color="text.secondary">W9 Status</Typography>
                          <Chip 
                            label={taxInfo.w9_submitted ? 'Submitted' : 'Not Submitted'} 
                            color={taxInfo.w9_submitted ? 'success' : 'default'}
                            size="small"
                          />
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                ) : (
                  <Paper
                    sx={{
                      p: 4,
                      textAlign: 'center',
                      backgroundColor: 'grey.50',
                      border: '2px dashed',
                      borderColor: 'grey.300'
                    }}
                  >
                    <ReceiptIcon sx={{ fontSize: 48, color: 'grey.400', mb: 2 }} />
                    <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                      No Tax Information
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Add your tax information for compliance
                    </Typography>
                    <Button
                      variant="contained"
                      startIcon={<AddIcon />}
                      onClick={handleOpenTaxDialog}
                    >
                      Add Tax Information
                    </Button>
                  </Paper>
                )}
              </Box>
            </CardContent>
          </Box>
        </TabPanel>

        {/* Appearance Tab */}
        <TabPanel value={tabValue} index="appearance">
        <Box>
            <CardContent sx={{ p: isMobile ? 2 : 3 }}>
              <Typography
                variant={isMobile ? "h6" : "h5"}
                sx={{ mb: 3, fontWeight: 700 }}
              >
                Appearance Settings
              </Typography>

              <Grid container spacing={isMobile ? 2 : 3}>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    select
                    label="Theme Mode"
                    value={appearanceSettings.darkMode}
                    onChange={(e) =>
                      setAppearanceSettings((prev) => ({
                        ...prev,
                        darkMode: e.target.value,
                      }))
                    }
                    variant="outlined"
                    size={isMobile ? "small" : "medium"}
                  >
                    <MenuItem value={null}>System Default</MenuItem>
                    <MenuItem value={true}>Dark</MenuItem>
                    <MenuItem value={false}>Light</MenuItem>
                  </TextField>
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    select
                    label="UI Density"
                    value={appearanceSettings.uiDensity}
                    onChange={(e) =>
                      setAppearanceSettings((prev) => ({
                        ...prev,
                        uiDensity: e.target.value,
                      }))
                    }
                    variant="outlined"
                    size={isMobile ? "small" : "medium"}
                  >
                    <MenuItem value="compact">Compact</MenuItem>
                    <MenuItem value="normal">Normal</MenuItem>
                    <MenuItem value="comfortable">Comfortable</MenuItem>
                  </TextField>
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    select
                    label="Font Family"
                    value={appearanceSettings.fontPreference}
                    onChange={(e) =>
                      setAppearanceSettings((prev) => ({
                        ...prev,
                        fontPreference: e.target.value,
                      }))
                    }
                    variant="outlined"
                    size={isMobile ? "small" : "medium"}
                  >
                    <MenuItem value="default">System Default</MenuItem>
                    <MenuItem value="roboto">Roboto</MenuItem>
                    <MenuItem value="open-sans">Open Sans</MenuItem>
                    <MenuItem value="montserrat">Montserrat</MenuItem>
                  </TextField>
                </Grid>

                <Grid item xs={12}>
                  <Divider sx={{ my: isMobile ? 2 : 3 }} />
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    Color Customization
                  </Typography>
                  <Box sx={{
                    display: "flex",
                    gap: 3,
                    flexDirection: isMobile ? "column" : "row",
                    alignItems: isMobile ? "center" : "flex-start"
                  }}>
                    {["primary", "secondary"].map((key) => (
                      <Box key={key} sx={{ textAlign: "center" }}>
                        <FormHelperText sx={{ mb: 1, textAlign: "center" }}>
                          {key.charAt(0).toUpperCase() + key.slice(1)} Color
                        </FormHelperText>
                        <SketchPicker
                          color={appearanceSettings.themeColors[key]}
                          onChangeComplete={(color) =>
                            setAppearanceSettings((prev) => ({
                              ...prev,
                              themeColors: { ...prev.themeColors, [key]: color.hex },
                            }))
                          }
                          width={isMobile ? "250px" : undefined}
                        />
                      </Box>
                    ))}
                  </Box>
                </Grid>

                <Grid item xs={12} sx={{ textAlign: isMobile ? "center" : "right" }}>
                  <Button
                    variant="contained"
                    size="large"
                    onClick={handleSaveAppearance}
                    disabled={saveSettingsMutation.isLoading}
                    fullWidth={isMobile}
                  >
                    {saveSettingsMutation.isLoading ? (
                      <CircularProgress size={24} />
                    ) : (
                      "Save Preferences"
                    )}
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Box>
        </TabPanel>

        {/* Notifications Tab */}
        <TabPanel value={tabValue} index="notif">
        <Box>
            <CardContent sx={{ p: isMobile ? 2 : 3 }}>
              <Typography
                variant={isMobile ? "h6" : "h5"}
                sx={{ mb: 3, fontWeight: 700 }}
              >
                Notification Settings
              </Typography>
              <NotifSettings />
            </CardContent>
          </Box>
        </TabPanel>

        {/* Contracts Tab */}
        <TabPanel value={tabValue} index="contracts">
          <UserContractView />
        </TabPanel>

        {/* Developers Tab */}
        <TabPanel value={tabValue} index="dev">
          <Box>
            <CardContent sx={{ p: isMobile ? 2 : 3 }}>
              <DeveloperPanel />
            </CardContent>
          </Box>
        </TabPanel>

        {/* Account Manager Tab */}
        <TabPanel value={tabValue} index="accounts">
          <AccountManagerTab />
        </TabPanel>
      </Box>

      {/* Wallet Dialog */}
      <WalletDialog
        open={walletDialog.open}
        mode={walletDialog.mode}
        wallet={walletDialog.wallet}
        walletData={newWallet}
        onClose={handleCloseWalletDialog}
        onSubmit={handleSubmitWallet}
        onChange={handleWalletChange}
        onSuccess={fetchWallets}
        onLinkBankSuccess={(walletData) => {
          showSnackbar("Bank account linked successfully!", "success");
          fetchWallets();
          handleCloseWalletDialog();
        }}
        onLinkBankError={(err) => {
          showSnackbar("Failed to link bank account.", "error");
        }}
        isMobile={isMobile}
        taxInfo={taxInfo}
        onOpenTaxDialog={handleOpenTaxDialog}
        isTaxInfoRequired={isTaxInfoRequired}
        isTaxInfoSufficient={isTaxInfoSufficient}
        getTaxRequirementMessage={getTaxRequirementMessage}
        isCreator={false}
        existingPayPalWallet={wallets?.find(w => w.wallet_type === 'paypal')}
        existingStripeWallet={wallets?.find(w => w.wallet_type === 'stripe')}
      />

      {/* Tax Information Dialog */}
      <TaxInfoDialog
        open={taxDialog}
        taxData={newTaxInfo}
        onClose={handleCloseTaxDialog}
        onSubmit={handleSaveTaxInfo}
        onChange={handleTaxChange}
        isMobile={isMobile}
      />

      {/* Legal Entity Warning Dialog */}
      <Dialog
        open={legalEntityWarningOpen}
        onClose={() => setLegalEntityWarningOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            border: "3px solid",
            borderColor: "warning.main",
          }
        }}
      >
        <DialogTitle sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          backgroundColor: "warning.50",
          color: "warning.dark",
          fontWeight: 700
        }}>
          <WarningIcon sx={{ fontSize: 32 }} />
          Legal Entity Configuration Required
          <Box sx={{ flex: 1 }} />
          <IconButton
            onClick={() => setLegalEntityWarningOpen(false)}
            size="small"
            sx={{ color: "warning.dark" }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Typography variant="body1" sx={{ mb: 2, fontWeight: 500 }}>
            Your company's legal entity name has not been configured yet. This is required for:
          </Typography>
          <Box component="ul" sx={{ pl: 3, mb: 3 }}>
            <Typography component="li" variant="body2" sx={{ mb: 1 }}>
              <strong>Legal contracts and agreements</strong> - All contracts will reference this name
            </Typography>
            <Typography component="li" variant="body2" sx={{ mb: 1 }}>
              <strong>Spending Power operations</strong> - Required for spending power applications and approvals
            </Typography>
            <Typography component="li" variant="body2" sx={{ mb: 1 }}>
              <strong>Financial transactions</strong> - Ensures proper business entity identification
            </Typography>
            <Typography component="li" variant="body2">
              <strong>Regulatory compliance</strong> - Required for business registration and tax purposes
            </Typography>
          </Box>
          <Box sx={{
            p: 2,
            backgroundColor: "info.50",
            borderRadius: 2,
            border: "1px solid",
            borderColor: "info.200"
          }}>
            <Typography variant="body2" color="info.dark">
              <strong>Note:</strong> This should be your official registered business name as it appears in government filings and legal documents.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, gap: 2 }}>
          <Button
            onClick={() => setLegalEntityWarningOpen(false)}
            color="inherit"
          >
            Remind Me Later
          </Button>
          <Button
            onClick={handleGoToTeamSettings}
            variant="outlined"
            startIcon={<TeamIcon />}
          >
            Go to Team Settings
          </Button>
          <Button
            onClick={handleConfigureLegalEntity}
            variant="contained"
            startIcon={<BusinessIcon />}
            sx={{ fontWeight: 600 }}
          >
            Configure Now
          </Button>
        </DialogActions>
      </Dialog>

      {/* Legal Entity Configuration Dialog */}
      <Dialog
        open={legalEntityDialogOpen}
        onClose={() => setLegalEntityDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          Configure Legal Entity Name
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Enter your company's official legal name as it appears in business registration documents.
          </Typography>
          <TextField
            fullWidth
            label="Legal Entity Name"
            value={legalEntityName}
            onChange={(e) => setLegalEntityName(e.target.value)}
            placeholder="e.g., Your Company LLC, Your Corp Inc."
            helperText="This will be used in all future contracts and legal documents"
            sx={{ mb: 2 }}
          />
          <Box sx={{
            p: 2,
            backgroundColor: "grey.50",
            borderRadius: 2,
            border: "1px solid",
            borderColor: "grey.200"
          }}>
            <Typography variant="caption" color="text.secondary">
              <strong>Examples:</strong><br />
              • "Acme Corporation Inc."<br />
              • "Smith & Associates LLC"<br />
              • "Global Tech Solutions Ltd."
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setLegalEntityDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleLegalEntityUpdate}
            disabled={updateLegalEntityMutation.isLoading || !legalEntityName.trim()}
            sx={{ fontWeight: 600 }}
          >
            {updateLegalEntityMutation.isLoading ? (
              <CircularProgress size={20} sx={{ color: "white" }} />
            ) : (
              "Save Legal Entity"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Spending Power Dialog */}
      <SpendingPowerDialog
        open={spendingDialogOpen}
        onClose={() => setSpendingDialogOpen(false)}
        ledgerEntries={ledgerEntries}
        ledgerLoading={ledgerLoading}
        ledgerError={ledgerError}
        ledgerErrorObj={ledgerErrorObj}
        companyId={userInfo?.company_id}
        isMobile={isMobile}
      />

      {/* Upload Dialog */}
      <UploadFileDialog
        open={uploadDialogOpen}
        onClose={() => setUploadDialogOpen(false)}
        onSuccess={(fileData) =>
          setUserData((prev) => ({ ...prev, phphref: fileData.url }))
        }
      />

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

/* ────────────────────────────────────────────────────────────────────────── */
/* Spending Power Dialog Component                                          */
/* ────────────────────────────────────────────────────────────────────────── */

const SpendingPowerDialog = ({
  open,
  onClose,
  ledgerEntries,
  ledgerLoading,
  ledgerError,
  ledgerErrorObj,
  companyId,
  isMobile = false,
}) => {
  const queryClient = useQueryClient();
  const [requestedSpendingPower, setRequestedSpendingPower] = useState("");
  const [requestedInterestRate, setRequestedInterestRate] = useState("");
  const [reason, setReason] = useState("");

  const postSpendingRequestMutation = useMutation(
    (input) => client.creditLedger.postRequest(input),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["spendingLedger", companyId]);
        onClose();
        setReason("");
        setRequestedSpendingPower("");
        setRequestedInterestRate("");
      },
      onError: (error) =>
        alert(error?.response?.data?.error || error.message),
    }
  );

  const handleSubmit = () => {
    postSpendingRequestMutation.mutate({
      company_id: companyId,
      requested_credit_line: Number(requestedSpendingPower),
      requested_interest_rate: Number(requestedInterestRate),
      reason: reason || "User request",
    });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : 3,
          width: "100%",
          maxWidth: isMobile ? "100%" : 1200,
          margin: isMobile ? 0 : undefined,
          height: isMobile ? "100%" : "auto"
        }
      }}
    >
      <DialogTitle sx={{
        fontSize: isMobile ? "1.25rem" : "1.5rem",
        p: isMobile ? 2 : 3
      }}>
        Adjust Spending Power & Discount Rate
      </DialogTitle>
      <DialogContent sx={{ p: isMobile ? 2 : 3 }}>
        <DialogContentText sx={{ mb: 2, fontSize: isMobile ? "0.875rem" : undefined }}>
          Below is the historical spending power ledger for your company. You can also request a change to your spending power limit and/or nominal discount rate.
        </DialogContentText>

        {ledgerLoading && <CircularProgress size={24} sx={{ m: 2 }} />}
        {ledgerError && (
          <Typography color="error" sx={{ fontSize: isMobile ? "0.875rem" : undefined }}>
            Error loading ledger: {ledgerErrorObj?.message}
          </Typography>
        )}
        {ledgerEntries?.length > 0 && (
          <Box sx={{
            maxHeight: isMobile ? 150 : 200,
            overflowY: "auto",
            mb: 2,
            border: "1px solid #ccc",
            borderRadius: 1,
            p: 1
          }}>
            {ledgerEntries.map((entry) => (
              <Box key={entry.id} sx={{ mb: 1, p: 1, borderBottom: "1px solid #eee" }}>
                <Typography variant={isMobile ? "caption" : "body2"}>
                  <strong>{entry.operation_type.toUpperCase()}</strong> on{" "}
                  {new Date(entry.created_at).toLocaleString()}
                </Typography>
                <Typography variant="caption" sx={{ fontSize: isMobile ? "0.7rem" : undefined }}>
                  Change: {entry.change_amount} | New Spending Power: {entry.new_limit || entry.spending_power_limit} |{" "}
                  New Discount Rate: {entry.new_interest_rate}%
                </Typography>
                {entry.reason && (
                  <Typography
                    variant="caption"
                    display="block"
                    sx={{ fontSize: isMobile ? "0.7rem" : undefined }}
                  >
                    Reason: {entry.reason}
                  </Typography>
                )}
              </Box>
            ))}
          </Box>
        )}

        <Grid container spacing={isMobile ? 2 : 2}>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Requested Spending Power"
              type="number"
              fullWidth
              size={isMobile ? "small" : "medium"}
              value={requestedSpendingPower}
              onChange={(e) => setRequestedSpendingPower(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Requested Discount Rate"
              type="number"
              fullWidth
              size={isMobile ? "small" : "medium"}
              value={requestedInterestRate}
              onChange={(e) => setRequestedInterestRate(e.target.value)}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Reason for Change"
              fullWidth
              multiline
              rows={2}
              size={isMobile ? "small" : "medium"}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{
        p: isMobile ? 2 : 3,
        flexDirection: isMobile ? "column-reverse" : "row",
        gap: isMobile ? 1 : 0
      }}>
        <Button
          onClick={onClose}
          fullWidth={isMobile}
          size={isMobile ? "large" : "medium"}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={postSpendingRequestMutation.isLoading}
          fullWidth={isMobile}
          size={isMobile ? "large" : "medium"}
        >
          {postSpendingRequestMutation.isLoading ? (
            <CircularProgress size={20} sx={{ color: "#fff" }} />
          ) : (
            "Submit Request"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default User;